import { useEffect, useState } from 'react';

import { env } from '@/shared/config/env';

const IMAGE_KEYS = [
  'primaryImageUrl',
  'primary_image_url',
  'productImageUrl',
  'product_image_url',
  'imageUrl',
  'image_url',
  'thumbnailUrl',
  'thumbnail_url',
  'primaryImage',
  'primary_image',
  'image',
  'images',
  'productImages',
  'product_images',
  'imageUrls',
  'image_urls',
] as const;

const NESTED_URL_KEYS = [
  'url',
  'imageUrl',
  'image_url',
  'src',
  'thumbnailUrl',
  'thumbnail_url',
  'path',
] as const;

/** Search results remember a working photo so the cart can reuse it. */
const rememberedByProductId = new Map<string, string>();
const listeners = new Set<() => void>();

export function rememberProductImage(productId: string, url: string): void {
  if (!productId || !/^https?:\/\//i.test(url)) return;
  if (rememberedByProductId.get(productId) === url) return;
  rememberedByProductId.set(productId, url);
  listeners.forEach((listener) => listener());
}

export function rememberedProductImage(productId: string): string {
  return rememberedByProductId.get(productId) ?? '';
}

/** Re-renders when a search hit stores a photo the cart can use. */
export function useRememberedProductImages(): ReadonlyMap<string, string> {
  const [images, setImages] = useState<ReadonlyMap<string, string>>(
    () => new Map(rememberedByProductId),
  );

  useEffect(() => {
    const sync = () => setImages(new Map(rememberedByProductId));
    listeners.add(sync);
    sync();
    return () => {
      listeners.delete(sync);
    };
  }, []);

  return images;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function isAbsolute(value: string): boolean {
  return /^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:');
}

function looksLikeImagePath(value: string): boolean {
  return value.startsWith('/') || /\.(png|jpe?g|webp|gif|avif|svg)(\?|#|$)/i.test(value);
}

function apiOrigin(): string {
  return env.apiBaseUrl.replace(/\/v1\/?$/, '').replace(/\/$/, '');
}

/** Cart lines send `catalog/products/...jpg`. Search already has the public S3 URL for that key. */
function mediaBase(): string {
  for (const url of rememberedByProductId.values()) {
    const index = url.indexOf('/catalog/');
    if (index > 0 && /^https?:\/\//i.test(url)) return url.slice(0, index + 1);
  }
  return '';
}

function toAbsolute(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '-') return '';
  if (isAbsolute(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (!looksLikeImagePath(trimmed)) return '';
  if (!trimmed.startsWith('/')) {
    const base = mediaBase();
    return base ? `${base}${trimmed.replace(/^\/+/, '')}` : '';
  }
  return `${apiOrigin()}${trimmed}`;
}

function isPrimary(value: unknown): boolean {
  const record = asRecord(value);
  if (!record) return false;
  return record.isPrimary === true || record.primary === true || record.is_primary === true;
}

function collect(value: unknown, depth = 0): string[] {
  if (depth > 4 || value == null) return [];
  if (typeof value === 'string') {
    const url = toAbsolute(value);
    return url ? [url] : [];
  }
  if (Array.isArray(value)) {
    const primary = value.filter(isPrimary);
    return [...primary, ...value].flatMap((item) => collect(item, depth + 1));
  }
  const record = asRecord(value);
  if (!record) return [];
  return NESTED_URL_KEYS.flatMap((key) => collect(record[key], depth + 1));
}

function unique(urls: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of urls) {
    if (seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

/** First usable product photo from a search hit or cart line. Absolute URLs win over storage paths. */
export function productImageFromRecord(record: Record<string, unknown>): string {
  const nested = asRecord(record.product);
  const sources = IMAGE_KEYS.flatMap((key) => [record[key], nested?.[key]]);
  const urls = unique(sources.flatMap((value) => collect(value)));
  const remote = urls.find((url) => /^https?:\/\//i.test(url));
  return remote ?? urls[0] ?? '';
}
